/**
 * Ably Client Singleton
 *
 * Creates and manages a single Ably Realtime client instance
 * with token authentication from backend.
 */

import * as Ably from 'ably';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { ensureValidToken, refreshAccessToken } from './token-refresh';
import { createLogger } from './logger';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const logger = createLogger('Ably');

let ablyClient: Ably.Realtime | null = null;

interface AblyTokenResponse {
  success: boolean;
  data: Ably.TokenRequest;
  timestamp: string;
}

/**
 * Makes the actual HTTP request to fetch an Ably token
 */
async function requestAblyToken(accessToken: string | null): Promise<AblyTokenResponse> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_URL}/auth/ably-token`, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  if (response.status === 401) {
    throw new AblyAuthError('Token rejected by server');
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch Ably token: ${response.statusText}`);
  }

  return (await response.json()) as AblyTokenResponse;
}

/** Sentinel error to distinguish 401s from other failures */
class AblyAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AblyAuthError';
  }
}

/**
 * Fetches a short-lived Ably token from the backend
 * Ensures JWT is fresh before requesting, retries once on 401
 */
async function fetchAblyToken(): Promise<Ably.TokenRequest> {
  // Ensure token is valid before fetching Ably token (handles stale/expired tokens)
  await ensureValidToken();

  const accessToken = useAuthStore.getState().accessToken;

  try {
    const result = await requestAblyToken(accessToken);

    if (!result.success) {
      throw new Error('Failed to fetch Ably token');
    }

    return result.data;
  } catch (error) {
    // On 401, refresh the JWT and retry once
    if (error instanceof AblyAuthError) {
      logger.warn('Ably token request got 401, refreshing JWT and retrying');
      await refreshAccessToken();
      const freshToken = useAuthStore.getState().accessToken;
      const retryResult = await requestAblyToken(freshToken);

      if (!retryResult.success) {
        throw new Error('Failed to fetch Ably token after refresh');
      }

      return retryResult.data;
    }

    throw error;
  }
}

/**
 * Creates or returns the existing Ably client instance
 * Uses token authentication with clientId set by backend
 */
export function getAblyClient(): Ably.Realtime {
  if (ablyClient) {
    return ablyClient;
  }

  // Initialize client with token auth
  ablyClient = new Ably.Realtime({
    authCallback: (_tokenParams, callback) => {
      void (async () => {
        try {
          const tokenRequest = await fetchAblyToken();
          callback(null, tokenRequest);
        } catch (error) {
          const errorInfo: Ably.ErrorInfo = {
            code: (error as Error).message.includes('Failed to fetch') ? 40100 : 50000,
            statusCode: (error as Error).message.includes('Failed to fetch') ? 401 : 500,
            message: (error as Error).message,
            name: 'Error'
          };
          callback(errorInfo, null);
        }
      })();
    },
    // Auto-reconnect on disconnect
    disconnectedRetryTimeout: 3000,
    suspendedRetryTimeout: 10000,
    echoMessages: false
  });

  // Connection state logging
  ablyClient.connection.on('connected', () => {
    logger.info('Connected to Ably', { clientId: ablyClient?.auth.clientId });
  });

  ablyClient.connection.on('disconnected', () => {
    logger.warn('Disconnected from Ably');
  });

  ablyClient.connection.on('failed', (error) => {
    logger.error('Connection failed', error);
  });

  ablyClient.connection.on('connecting', () => {
    logger.debug('Connecting to Ably...');
  });

  return ablyClient;
}

/**
 * Returns the current Ably connection state WITHOUT creating a client as a
 * side effect (unlike `getAblyClient()`, which lazily connects). Used by the
 * error reporter (Story 03-03): checking connection health from a global
 * crash handler must never itself open a new Ably connection.
 */
export function peekAblyConnectionState(): Ably.ConnectionState | null {
  return ablyClient?.connection.state ?? null;
}

/**
 * Subscribes to the Ably `connected` connection transition (Story 04-02, D10).
 *
 * The connection-state listeners in `getAblyClient()` only log; game-sync needs
 * to react to a (re)connection by resyncing from server truth. This exposes the
 * transition to subscribers without disturbing the existing log lines.
 *
 * Uses the shared client via `getAblyClient()` (returns the existing singleton
 * on a game page). `connection.on` fires only on transitions after subscription,
 * so a connection already `connected` at subscribe time does not fire — which is
 * why the reconnect resync never double-fires with the page's initial load.
 *
 * @param handler - Called on each `connected` transition.
 * @returns Unsubscribe function; call it on cleanup.
 */
export function onAblyConnected(handler: () => void): () => void {
  const client = getAblyClient();
  const listener = (): void => {
    handler();
  };
  client.connection.on('connected', listener);
  return () => {
    client.connection.off('connected', listener);
  };
}

/**
 * Closes the Ably client connection
 * Use on app unmount or logout
 */
export function closeAblyClient(): void {
  if (ablyClient) {
    ablyClient.close();
    ablyClient = null;
  }
}
